import React, { useEffect, useState } from 'react';
import { Save, Plus } from 'lucide-react';
import { adminAPI } from '../../api/admin';
import toast from 'react-hot-toast';

const SettingsAdminPage: React.FC = () => {
  const [settings, setSettings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    description: '',
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState({
    paystack_public_key: '',
    paystack_secret_key: '',
    paypal_client_id: '',
    paypal_client_secret: '',
    paypal_mode: 'sandbox',
    stripe_publishable_key: '',
    stripe_secret_key: '',
    stripe_webhook_secret: '',
    google_pay_merchant_id: '',
    google_pay_merchant_name: '',
  });
  const [savingPayments, setSavingPayments] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [savingMaintenance, setSavingMaintenance] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

const fetchSettings = async () => {
  try {
    setIsLoading(true);
    const res = await adminAPI.getSiteSettings();
    if (res.success) {
      const data = res.data || [];
      setSettings(data);

      const getVal = (key: string) =>
        data.find((s: any) => s.key === key)?.value || '';

      setPaymentSettings({
        paystack_public_key: getVal('paystack_public_key'),
        paystack_secret_key: getVal('paystack_secret_key'),
        paypal_client_id: getVal('paypal_client_id'),
        paypal_client_secret: getVal('paypal_client_secret'),
        paypal_mode: getVal('paypal_mode') || 'sandbox',
        stripe_publishable_key: getVal('stripe_publishable_key'),
        stripe_secret_key: getVal('stripe_secret_key'),
        stripe_webhook_secret: getVal('stripe_webhook_secret'),
        google_pay_merchant_id: getVal('google_pay_merchant_id'),
        google_pay_merchant_name: getVal('google_pay_merchant_name'),
      });

      const maintenanceSetting = data.find((s: any) => s.key === 'maintenance_mode');
      setMaintenanceMode(maintenanceSetting?.value === 'true');
    }
  } catch (err) {
    console.error('fetchSettings error:', err);
    toast.error('Failed to fetch settings');
  } finally {
    setIsLoading(false);
  }
};

  const handleSave = async (key: string, description?: string) => {
    try {
      await adminAPI.updateSiteSetting({ key, value: editValue, description });
      toast.success('Setting saved');
      setEditingKey(null);
      fetchSettings();
    } catch {
      toast.error('Failed to save setting');
    }
  };

  const handleNewSetting = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await adminAPI.updateSiteSetting(newSetting);
      toast.success('Setting created');
      setShowNewForm(false);
      setNewSetting({ key: '', value: '', description: '' });
      fetchSettings();
    } catch {
      toast.error('Failed to create setting');
    }
  };

  const handleSavePaymentSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSavingPayments(true);
      for (const [key, value] of Object.entries(paymentSettings)) {
        await adminAPI.updateSiteSetting({ key, value });
      }
      toast.success('Payment settings saved');
    } catch {
      toast.error('Failed to save payment settings');
    } finally {
      setSavingPayments(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      setSavingMaintenance(true);
      const newValue = !maintenanceMode;
      await adminAPI.updateSiteSetting({
        key: 'maintenance_mode',
        value: String(newValue),
        description: 'When true, shows coming soon page to visitors',
      });
      setMaintenanceMode(newValue);
      toast.success(newValue ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
    } catch {
      toast.error('Failed to update maintenance mode');
    } finally {
      setSavingMaintenance(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs tracking-widest uppercase text-nude mb-1">
            Super Admin
          </p>
          <h1 className="font-serif text-3xl text-primary">Site Settings</h1>
        </div>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={14} />
          <span>New Setting</span>
        </button>
      </div>

      {/* Maintenance Mode Toggle */}
      <div className="bg-white border border-light p-6 flex items-center justify-between">
        <div>
          <h2 className="font-serif text-xl text-primary mb-1">
            Maintenance Mode
          </h2>
          <p className="text-xs text-nude tracking-wide">
            When enabled, visitors see a "Coming Soon" page. Admins can still access the dashboard.
          </p>
        </div>
        <button
          onClick={handleToggleMaintenance}
          disabled={savingMaintenance}
          className={`relative w-14 h-7 rounded-full transition-colors disabled:opacity-50 ${
            maintenanceMode ? 'bg-primary' : 'bg-light'
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
              maintenanceMode ? 'translate-x-7' : ''
            }`}
          />
        </button>
      </div>

      {showNewForm && (
        <form
          onSubmit={handleNewSetting}
          className="bg-white border border-light p-6 space-y-4"
        >
          <h2 className="font-serif text-lg text-primary">New Setting</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Key
              </label>
              <input
                value={newSetting.key}
                onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value })}
                className="input-luxury"
                placeholder="site_name"
                required
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Value
              </label>
              <input
                value={newSetting.value}
                onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                className="input-luxury"
                required
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                Description
              </label>
              <input
                value={newSetting.description}
                onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })}
                className="input-luxury"
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <button type="submit" className="btn-primary">
              Create Setting
            </button>
            <button
              type="button"
              onClick={() => setShowNewForm(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-light overflow-x-auto">
        {isLoading ? (
          <div className="p-6 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-12 rounded" />
            ))}
          </div>
        ) : settings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-xs tracking-widest uppercase text-nude">
              No settings configured
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-light">
                {['Key', 'Value', 'Description', 'Updated', 'Actions'].map((h) => (
                  <th
                    key={h}
                    className="px-6 py-3 text-left text-xs tracking-widest uppercase text-nude"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {settings.map((setting) => (
                <tr
                  key={setting.id}
                  className="border-b border-light hover:bg-light/50 transition-colors"
                >
                  <td className="px-6 py-4 text-xs font-mono font-medium text-primary">
                    {setting.key}
                  </td>
                  <td className="px-6 py-4">
                    {editingKey === setting.key ? (
                      <input
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="border border-primary px-2 py-1 text-xs outline-none w-full"
                        autoFocus
                      />
                    ) : (
                      <span className="text-xs text-secondary">
                        {setting.value}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {setting.description || '—'}
                  </td>
                  <td className="px-6 py-4 text-xs text-nude">
                    {setting.updated_at
                      ? new Date(setting.updated_at).toLocaleDateString()
                      : '—'}
                  </td>
                  <td className="px-6 py-4">
                    {editingKey === setting.key ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleSave(setting.key, setting.description)}
                          className="text-green-600 hover:text-green-700 transition-colors"
                        >
                          <Save size={14} />
                        </button>
                        <button
                          onClick={() => setEditingKey(null)}
                          className="text-nude hover:text-primary transition-colors text-xs tracking-widest uppercase"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setEditingKey(setting.key);
                          setEditValue(setting.value || '');
                        }}
                        className="text-xs tracking-widest uppercase text-nude hover:text-primary transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Payment Methods */}
      <div className="bg-white border border-light p-6 mt-6">
        <h2 className="font-serif text-xl text-primary mb-6">
          Payment Method Configuration
        </h2>
        <form onSubmit={handleSavePaymentSettings} className="space-y-8">
          {/* Paystack */}
          <div>
            <h3 className="text-xs tracking-widest uppercase text-nude mb-4 pb-2 border-b border-light">
              Paystack
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Public Key
                </label>
                <input
                  value={paymentSettings.paystack_public_key}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    paystack_public_key: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="pk_live_..."
                />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={paymentSettings.paystack_secret_key}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    paystack_secret_key: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="sk_live_..."
                />
              </div>
            </div>
          </div>

          {/* PayPal */}
          <div>
            <h3 className="text-xs tracking-widest uppercase text-nude mb-4 pb-2 border-b border-light">
              PayPal
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Client ID
                </label>
                <input
                  value={paymentSettings.paypal_client_id}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    paypal_client_id: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="AV..."
                />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Client Secret
                </label>
                <input
                  type="password"
                  value={paymentSettings.paypal_client_secret}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    paypal_client_secret: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="EG..."
                />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Mode
                </label>
                <select
                  value={paymentSettings.paypal_mode}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    paypal_mode: e.target.value
                  })}
                  className="input-luxury text-xs"
                >
                  <option value="sandbox">Sandbox</option>
                  <option value="live">Live</option>
                </select>
              </div>
            </div>
          </div>

          {/* Stripe */}
          <div>
            <h3 className="text-xs tracking-widest uppercase text-nude mb-4 pb-2 border-b border-light">
              Stripe
            </h3>
            <p className="text-xs text-nude mb-4 leading-relaxed">
              Powers the inline "Pay with Card" fields (Visa, Mastercard, Apple Pay, Google Pay) for all customers outside Nigeria.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Publishable Key
                </label>
                <input
                  value={paymentSettings.stripe_publishable_key}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    stripe_publishable_key: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="pk_live_..."
                />
              </div>
              <div>
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Secret Key
                </label>
                <input
                  type="password"
                  value={paymentSettings.stripe_secret_key}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    stripe_secret_key: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="sk_live_..."
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs tracking-widest uppercase text-nude block mb-2">
                  Webhook Signing Secret
                </label>
                <input
                  type="password"
                  value={paymentSettings.stripe_webhook_secret}
                  onChange={(e) => setPaymentSettings({
                    ...paymentSettings,
                    stripe_webhook_secret: e.target.value
                  })}
                  className="input-luxury text-xs"
                  placeholder="whsec_..."
                />
                <p className="text-xs text-nude mt-2 leading-relaxed">
                  From Stripe Dashboard → Developers → Webhooks → your endpoint. Required to verify that payment confirmations genuinely came from Stripe.
                </p>
              </div>
            </div>
          </div>

          {/* Apple Pay & Google Pay Notice */}
          <div>
            <h3 className="text-xs tracking-widest uppercase text-nude mb-4 pb-2 border-b border-light">
              Apple Pay & Google Pay
            </h3>
            <div className="bg-light p-4 space-y-3">
              <p className="text-xs text-secondary leading-relaxed">
                For Nigerian customers, Apple Pay and Google Pay are automatically enabled through Paystack.
                To activate them, go to your Paystack Dashboard → Settings → Payment Methods
                and enable Apple Pay and Google Pay. They will automatically appear in the
                checkout popup for supported devices.
              </p>
              <p className="text-xs text-secondary leading-relaxed">
                For all other customers, Apple Pay and Google Pay are automatically enabled through Stripe
                once configured above — no separate setup needed here. To turn them on, go to your Stripe
                Dashboard → Settings → Payment Methods and enable Apple Pay and Google Pay. They'll appear
                automatically in the card payment fields on supported devices and browsers.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={savingPayments}
            className="btn-primary disabled:opacity-50"
          >
            {savingPayments ? 'Saving...' : 'Save Payment Settings'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SettingsAdminPage;